import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts/TenantProvider'
import { supabase } from '@/lib/supabase'
import { X, Check, Loader2, Globe, AlertTriangle, ArrowRight } from 'lucide-react'

/* ============================================================
   PublishFlowModal — Confirm + publish website changes
   Phase 3C: Three-step publish flow (review → confirm → deploy)
   ============================================================ */

interface PublishFlowModalProps {
  open: boolean
  onClose: () => void
  pendingChanges?: string[]
}

const EMPTY_CHANGES: string[] = []

type Stage = 'review' | 'confirm' | 'done'

function usePublishSite(tenantId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      // Update tenant published_at timestamp
      const { error } = await supabase
        .from('tenants')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', tenantId!)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['site-content'] })
    },
  })
}

export default function PublishFlowModal({
  open,
  onClose,
  pendingChanges = EMPTY_CHANGES,
}: PublishFlowModalProps) {
  const { tenant } = useTenant()
  const [stage, setStage] = useState<Stage>('review')
  const publishMutation = usePublishSite(tenant?.id)

  if (!open) return null

  const close = () => {
    setStage('review')
    publishMutation.reset()
    onClose()
  }

  const handlePublish = async () => {
    setStage('confirm')
    try {
      await publishMutation.mutateAsync()
      setStage('done')
    } catch {
      setStage('review')
    }
  }

  const changes =
    pendingChanges.length > 0
      ? pendingChanges
      : [
          'Content sections',
          'Site theme & branding',
          'SEO metadata',
          'Gallery images',
          'FAQ content',
        ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-6 shadow-2xl shadow-slate-200/60">
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-xl ${stage === 'done' ? 'border border-emerald-200 bg-emerald-50' : 'border border-blue-100 bg-[#cde5ff]'}`}
            >
              {stage === 'done' ? (
                <Check className="w-5 h-5 text-emerald-600" />
              ) : stage === 'confirm' ? (
                <Loader2 className="w-5 h-5 animate-spin text-origin-blue" />
              ) : (
                <Globe className="w-5 h-5 text-origin-blue" />
              )}
            </div>
            <div>
              <h3
                className="text-[20px] text-text-primary"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
              >
                {stage === 'done'
                  ? 'Published!'
                  : stage === 'confirm'
                    ? 'Publishing…'
                    : 'Publish Changes'}
              </h3>
              <p className="text-[11px] font-mono text-text-secondary">
                {stage === 'done'
                  ? 'Your changes are now live'
                  : stage === 'confirm'
                    ? 'Deploying updates…'
                    : 'Review before making changes live'}
              </p>
            </div>
          </div>
          {stage !== 'confirm' && (
            <button
              onClick={close}
              className="rounded-lg p-1.5 text-text-tertiary transition-colors hover:bg-surface-hover hover:text-text-primary"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Review stage */}
        {stage === 'review' && (
          <>
            {/* Warning */}
            <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-warning-text" />
              <p className="text-[11px] leading-relaxed text-amber-900">
                Publishing will make the following changes visible on your public website. This
                action cannot be undone.
              </p>
            </div>

            {/* Change list */}
            <div className="space-y-1 mb-4">
              <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.12em] text-text-secondary">
                Changes to publish
              </p>
              {changes.map((change, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2"
                >
                  <ArrowRight className="h-3 w-3 text-origin-blue" />
                  <span className="text-[12px] text-text-primary">{change}</span>
                </div>
              ))}
            </div>

            {/* Publish info */}
            <div className="mb-5 rounded-lg border border-border bg-bg-tertiary px-3 py-2">
              <p className="text-[10px] font-mono text-text-secondary">
                Publishing to:{' '}
                <span className="text-text-primary">
                  {tenant?.custom_domain
                    ? tenant.custom_domain
                    : `${window.location.origin}/${tenant?.slug ?? 'your-salon'}`}
                </span>
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={close}
                className="flex-1 rounded-lg border border-border bg-surface px-4 py-2.5 text-[13px] text-text-primary transition-colors hover:bg-surface-hover"
              >
                Cancel
              </button>
              <button
                onClick={handlePublish}
                className="glow-pulse flex-1 rounded-lg border border-origin-blue bg-origin-blue px-4 py-2.5 text-[13px] text-white transition-colors hover:bg-[#004b81]"
              >
                Publish Now
              </button>
            </div>
          </>
        )}

        {/* Confirm (loading) stage */}
        {stage === 'confirm' && (
          <div className="flex flex-col items-center py-8 gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#cde5ff] bg-[#f0f7ff]">
              <Loader2 className="w-6 h-6 animate-spin text-origin-blue" />
            </div>
            <p className="text-[12px] font-mono text-text-secondary">
              Deploying changes to your website…
            </p>
          </div>
        )}

        {/* Done stage */}
        {stage === 'done' && (
          <div className="flex flex-col items-center py-6 gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50">
              <Check className="w-7 h-7 text-emerald-600" />
            </div>
            <div className="text-center">
              <p
                className="text-[14px] text-text-primary"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Changes Published Successfully
              </p>
              <p className="mt-1 text-[11px] font-mono text-text-secondary">
                Your website has been updated
              </p>
            </div>
            <button
              onClick={close}
              className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-6 py-2.5 text-[13px] text-emerald-700 transition-colors hover:bg-emerald-100"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
